import Enumerable from "linq";
import { AbstractRichTextItem } from "./AbstractRichTextItem";
import { TypeKeys } from "./TypeKeys";
import { IPasteRequestable } from "./IPasteRequestable";
import { RelationalActionParameter } from "./RelationalActionParameter";
import { PaintStyle, TextAlignKinds } from "../../drawing";
import { IPasteApplicable } from "./IPasteApplicable";
import { TextLengthCalculator } from "../../drawing/TextLengthCalculator";
import store from "../../../store";
import { DomainRegistry } from "../../../domain";
import { IConfiguration } from "../../../domain/configuration";
import { IRelayoutHeightSpecifiable } from "./IRelayoutHeightSpecifiable";
import { IMksTemplateExecutable } from "./IMksTemplateExecutable";
import { IDynamicTemplateExecutable } from "./IDynamicTemplateExecutable";
import { AutoNotePageKinds } from "../AutoNotePageKinds";
import { RichTextBoxInputMethodKinds } from "../inputMethodKinds/RichTextBoxInputMethodKinds";
import { RichTextBoxInputMethodOptionKinds } from "../inputMethodOptionKinds/RichTextBoxInputMethodOptionKinds";
import { IImeControllable } from "./IImeControllable";
import { ImeModeKinds } from "../ImeModeKinds";
import { IEmptyValidatable } from "./IEmptyValidatable";

export class RichTextBoxItem extends AbstractRichTextItem implements
  IImeControllable,
  IPasteRequestable,
  IPasteApplicable,
  IRelayoutHeightSpecifiable,
  IMksTemplateExecutable,
  IDynamicTemplateExecutable,
  IEmptyValidatable {
  constructor(value: any) {
    super(TypeKeys.RichTextBoxItem, value);
  }

  public imeMode: ImeModeKinds = ImeModeKinds.AllOn;

  public paintStyle: PaintStyle = new PaintStyle();
  public paintStyleOnEditing: PaintStyle = new PaintStyle();

  public enableSingleEmptyCheck: boolean = false;
  public enableEmptyCheckConfirmation: boolean = false;
  public alertGroupKey: string = null;
  public parentAlertInformations: RelationalActionParameter[] = [];

  /** 可変対応 */
  public originalHeight: number = null;
  public adjustedHeight: number = null;
  public relayoutHeight: number = null;

  public pasteDestinations: RelationalActionParameter[] = [];
  public isFreeInputable: boolean = true;

  /** MKSテンプレート */
  public mksTemplateFormats: RelationalActionParameter[] = [];
  public mksTemplateRegisteredId: string = null;
  /** DYNAMICテンプレート */
  public dynamicTemplateFormats: RelationalActionParameter[] = [];
  public dynamicTemplateRegisteredId: string = null;

  /** 外部テンプレート対応のI/Fで必要なので取り急ぎ定義しているが使っていない(cloneもしていない) */
  enabledAutoAdjustFontSize: boolean = null;
  autoNotePageSize: number = null;
  autoNotePageKind: AutoNotePageKinds = null;

  /** 入力方法 */
  public inputMethodKind: RichTextBoxInputMethodKinds = RichTextBoxInputMethodKinds.FreeInput;
  /** 入力方法の選択肢の種別 */
  public inputMethodOptionKind: RichTextBoxInputMethodOptionKinds = RichTextBoxInputMethodOptionKinds.Normal;

  public isLineSpacingNarrowForLogicalFont: boolean = false;
  public executionMode: string = null;

  protected createCloneInstance(): RichTextBoxItem {
    return new RichTextBoxItem(this.originalValue);
  }

  protected applyMembers(clone: RichTextBoxItem): void {
    super.applyMembers(clone);
    clone.imeMode = this.cloneValue(this.imeMode);
    clone.inputMethodKind = this.cloneValue(this.inputMethodKind);
    clone.inputMethodOptionKind = this.cloneValue(this.inputMethodOptionKind);
    clone.paintStyle = this.cloneValue(this.paintStyle);
    clone.paintStyleOnEditing = this.cloneValue(this.paintStyleOnEditing);
    clone.enableSingleEmptyCheck = this.cloneValue(this.enableSingleEmptyCheck);
    clone.enableEmptyCheckConfirmation = this.cloneValue(this.enableEmptyCheckConfirmation);
    clone.alertGroupKey = this.cloneValue(this.alertGroupKey);
    clone.parentAlertInformations = this.cloneValue(this.parentAlertInformations);
    clone.originalHeight = this.cloneValue(this.originalHeight);
    clone.adjustedHeight = this.cloneValue(this.adjustedHeight);
    clone.relayoutHeight = this.cloneValue(this.relayoutHeight);
    clone.pasteDestinations = this.cloneValue(this.pasteDestinations);
    clone.isFreeInputable = this.cloneValue(this.isFreeInputable);
    clone.shouldInputFree = this.cloneValue(this.shouldInputFree);
    clone.mksTemplateFormats = this.cloneValue(this.mksTemplateFormats);
    clone.mksTemplateRegisteredId = this.cloneValue(this.mksTemplateRegisteredId);
    clone.dynamicTemplateFormats = this.cloneValue(this.dynamicTemplateFormats);
    clone.dynamicTemplateRegisteredId = this.cloneValue(this.dynamicTemplateRegisteredId);
    clone.isLineSpacingNarrowForLogicalFont = this.cloneValue(this.isLineSpacingNarrowForLogicalFont);
    clone.executionMode = this.cloneValue(this.executionMode);
  }

  get shouldInputFree(): boolean {
    return this.inputMethodKind === RichTextBoxInputMethodKinds.FreeInput;
  }

  set shouldInputFree(value: boolean) {
    this.inputMethodKind = value
      ? RichTextBoxInputMethodKinds.FreeInput
      : RichTextBoxInputMethodOptionKinds.getDefualtInputMethodKind(this.inputMethodOptionKind);
  }

  get isEmpty(): boolean {
    return !this.value || !this.plainText;
  }

  isValueUpdated(item: any): boolean {
    if (!item) { return true; }
    if (this.isEmpty && item.isEmpty) { return false; }
    return this.value !== item.value;
  }

  getGroupSelectKey(): string {
    return null;
  }

  isPasteKeyMatch(parameter: RelationalActionParameter): boolean {
    return (parameter.key && parameter.key === this.name && parameter.subKey === null);
  }

  applyPastedValue(text: string, isChecked: boolean, richTextAsHtml: string): void {
    this.value = richTextAsHtml
      ? richTextAsHtml
      : this.toHtmlTextFromPlainText(text);
    this.adjustObjectHeight();
  }

  private toHtmlTextFromPlainText(text: string): string {
    // TODO: implement
    // 可変・折り返しの処理どうする？
    if (!text) { return "<p><br></p>"; }
    const lineTexts = text.split("\n");
    const texts = lineTexts.map(l => this.escapeTextForHtml(l));
    return texts.map(t => this.paragraphTagWithAttributes((t !== "" ? this.spanTagWithAttributes(t) : "<br>"))).join("");
  }

  private escapeTextForHtml(text: string): string {
    if (!text) { return ""; }
    return text
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/ /g, "&nbsp;")
      .replace(/\t/g, (s, i, t) => this.replaceTabsWithSpaces(i, t));
  }

  private paragraphTagWithAttributes(text: string): string {
    switch (this.textStyle.textAlign) {
      case TextAlignKinds.TopLeft:
        return '<p class="ql-align-left">' + text + "</p>";
      case TextAlignKinds.TopCenter:
        return '<p class="ql-align-center">' + text + "</p>";
      case TextAlignKinds.TopRight:
        return '<p class="ql-align-right">' + text + "</p>";
      default:
        return "<p>" + text + "</p>";
    }
  }

  private spanTagWithAttributes(text: string): string {
    return '<span style="' +
      "font-family: " + this.toEscapeFontFamily() + "; " +
      "font-size: " + this.textStyle.baseFontSize  + "px; " +
      "color: " + this.textStyle.color.value + ";" +
      '">' + text + "</span>";
  }

  private toEscapeFontFamily(): string {
    switch (this.textStyle.fontFamily) {
      case "serif":
      case "sans-serif":
      case "cursive":
      case "fantasy":
      case "monospace":
      case "Monospaced":  // Javaの論理フォントであるが、とりあえずエスケープや変換を行わない（Quill側にまかせる）
        return this.textStyle.fontFamily;
      default :
        return "'" + this.textStyle.fontFamily + "'";
    }
  }

  isPasteApplicable(isQuoting: boolean): boolean {
    return true;
  }

  pasteTo(item: IPasteApplicable): void {
    const htmlText = this.valueToText(this.value);
    const extractedText = this.extractPlainText(htmlText);
    item.applyPastedValue(extractedText, undefined, this.value);
  }

  private extractPlainText(htmlText: string): string {
    if (!htmlText) { return ""; }
    // TODO: implement
    return htmlText
      .replace(/<\/p>$/g, "")
      .replace(/<\/p>/g, "\n")
      .replace(/<[^>]*>/g, "")
      .replace(/&lt;/g, "<")
      .replace(/&gt;/g, ">")
      .replace(/&amp;/g, "&")
      .replace(/&nbsp;/g, " ")
      .replace(/\t/g, (s, i, t) => this.replaceTabsWithSpaces(i, t));
  }

  replaceTabsWithSpaces(index: number, text: string): string {
    const startIndex = text.lastIndexOf("\n", index) + 1;
    const baseText = text.substring(startIndex, index);
    const baseIndex = TextLengthCalculator.calculateLength(baseText) * 2;  // 全角を1、半角を0.5としているため
    const length = this.tabReplacementSpacesCount - (baseIndex % this.tabReplacementSpacesCount);
    return Array(length + 1).join(" ");
  }

  get tabReplacementSpacesCount(): number {
    const configuration = store.getters.configuration as IConfiguration;
    return configuration.behavior.document.editor.objects.richTextBox.tabReplacementSpacesCount;
  }

  get plainText(): string {
    const htmlText = this.valueToText(this.value);
    return this.extractPlainText(htmlText);
  }

  set plainText(text: string) {
    this.value = this.toHtmlTextFromPlainText(text);
  }

  getTextLength(): number {
    return this.plainText.length;
  }

  getValueForExternalScript(): string {
    return this.plainText;
  }

  setValueFromExternalScript(value: string): void {
    this.plainText = value;
  }

  adjustFontSize(): void {
    // IExternalScriptAssociatableのために空実装
  }

  adjustObjectHeight(): void {
    this.updateAdjustedHeight();
  }

  processTemporaryElement<T>(process: (element: HTMLElement) => T): T {
    const element = this.htmlStringToElement(this.value);
    if (!element) { return null; }
    const style = this.createMeasureStyleElement(this.geometry.rectangle.width);
    document.head.appendChild(style);
    document.body.appendChild(element);
    const ret = process(element);
    document.head.removeChild(style);
    document.body.removeChild(element);
    return ret;
  }

  updateAdjustedHeight(): void {
    this.adjustedHeight = this.getAdjustedHeight();
    if (this.isRelayoutableByHeight) {
      this.geometry.rectangle.size.height = Math.max(this.adjustedHeight, this.originalHeight);
    }
  }

  get adjustedHeightMagnification(): number {
    const configuration = store.getters.configuration as IConfiguration;
    return configuration.behavior.document.editor.objects.richTextBox.adjustedHeightMagnification;
  }

  get adjustedHeightMargin(): number {
    const configuration = store.getters.configuration as IConfiguration;
    return configuration.behavior.document.editor.objects.richTextBox.adjustedHeightMargin;
  }

  getAdjustedHeight(): number {
    try {
      const repository = DomainRegistry.document.getRichTextRepository();
      return repository.getAdjustedHeight(this.executionMode, this.geometry.rectangle.width, (this.originalHeight * this.adjustedHeightMagnification) + this.adjustedHeightMargin, this.value);
    } catch (e) {
      return null;
    }
  }

  private htmlStringToElement(text: string): HTMLDivElement {
    const domParser = new DOMParser();
    const documentObj = domParser.parseFromString(text, "text/html");
    if (documentObj.getElementsByTagName("parsererror").length !== 0) { return; }
    const div = document.createElement("div");
    div.setAttribute("class", "rich-text-box-item-measure-root");
    if (!!documentObj.body) {
      let lastFont = {
        name: this.textStyle.fontFamily,
        size: this.textStyle.baseFontSize,
        height: this.toFontHeight(this.textStyle.fontFamily, this.textStyle.baseFontSize)
      };
      let lastLineFontSize = 0;
      Enumerable.from(documentObj.body.childNodes).forEach((e) => {
        this.sanitizeNode(e);
        const clone = e.cloneNode(true) as HTMLElement;
        if (clone.firstElementChild !== null && clone.firstElementChild.tagName.toUpperCase() === "BR") {
          clone.style.lineHeight = this.createBrBlockLineHeightStyle(lastFont.height, lastLineFontSize);
        } else {
          const font = this.createBlockFont(clone.childNodes, lastFont);
          lastFont = font.last;
          const lineHeightValue = this.toLineHeightValue(font.max, clone);
          lastLineFontSize = lineHeightValue.lineFontSize;
          clone.style.lineHeight = this.createLineHeightStyle(lineHeightValue);
        }
        div.appendChild(clone);
      });
    }
    return div;
  }

  private sanitizeNode(node: ChildNode): void {
    Enumerable.from(node.childNodes).forEach((e) => {
      this.sanitizeNode(e);
    });
    if (node.nodeType === Node.ELEMENT_NODE) {
      const element = node as HTMLElement;
      switch (element.tagName.toUpperCase()) {
        // Quillを拡張した場合確認が必要
        case "P":
          if (element.childNodes.length === 0) {
            element.appendChild(document.createElement("br"));
          }
          break;
        case "SPAN":
        case "STRONG":
        case "EM":
        case "U":
        case "S":
        case "SUP":
        case "SUB":
          break;
        default:
          node.remove();
          return;
      }
    }
  }

  private createMeasureStyleElement(width: number): HTMLStyleElement {
    const element = document.createElement("style");
    element.setAttribute("type", "text/css");
    // quill.snow.cssやRichTextBoxObject.vueのCSSが修正された場合確認が必要
    element.innerHTML = "\
    .rich-text-box-item-measure-root {\
      visibility: hidden;\
      position: fixed;\
      width: " + width + "px;\
      font-family: Helvetica, Arial, sans-serif;\
      font-size: 8px;\
      line-height: 1;\
      word-break: normal;\
      white-space: pre-wrap;\
      word-wrap: break-word;\
    }\
    .rich-text-box-item-measure-root p {\
      line-height: inherit;\
    }";
    return element;
  }

  createBlockFont(childNodes: NodeListOf<ChildNode>, defaultFont: { name: string, size: number, height: number }): { max: { name: string, size: number, height: number }, last: { name: string, size: number, height: number } } {
    let lastFont = defaultFont;
    const maxFont = Enumerable.from(childNodes)
      .where(c => c.nodeType === 1)
      .select(c => c as HTMLElement)
      .select(c => {
        const fontSize = parseFloat(c.style.fontSize.replace("px", ""));
        if (!isNaN(fontSize)) {
          lastFont = {
            name: c.style.fontFamily,
            size: fontSize,
            height: this.toFontHeight(c.style.fontFamily, fontSize)
          };
        }
        return lastFont;
      })
      .orderByDescending(c => c)
      .firstOrDefault(null, lastFont);
    return { max: maxFont, last: lastFont };
  }

  toFontHeight(fontFamily: string, fontSize: number): number {
    // C#のFont.Height相当を返す(フォントの行間らしい)
    // 選択できるフォント範囲ではメイリオのみ異なる模様
    switch (fontFamily) {
      case "メイリオ":
        return Math.ceil(fontSize * 1.5);
      default:
        return fontSize;
    }
  }

  toLineHeightValue(font: { name: string, size: number, height: number }, element: Element): { lineFontSize: number, lineSpace: number } {
    const fontFamilyCoefficient = this.createBlockLineHeightCoefficientFromFontFamily(element);
    const fontSizeCoefficient = this.toValueConsideringRoundingError(this.createLineHeightCoefficientFromFontSize(font.size) + (fontFamilyCoefficient - 1), 3);
    const lineFontSize = this.toValueConsideringRoundingError((font.size * fontFamilyCoefficient) + fontSizeCoefficient, 3);
    const attribute = element.attributes.getNamedItem("data-line-space");
    if (!attribute) {
      return { lineFontSize, lineSpace: 0 };
    }
    const lineSpace = parseInt(attribute.value, 10);
    return isNaN(lineSpace)
      ? { lineFontSize, lineSpace: 0 }
      : { lineFontSize, lineSpace: this.createLineHeightFromLineSpace(font.height, Math.floor(lineSpace / 2)) };
  }

  createBrBlockLineHeightStyle(fontSize: number, lineHeight: number): string {
    return Math.max(fontSize + 1, lineHeight) + "px";
  }

  createLineHeightStyle(value: { lineFontSize: number, lineSpace: number }): string {
    return value.lineSpace === 0
      ? value.lineFontSize + "px"
      : "calc(" + value.lineFontSize + "px + " + value.lineSpace + "px)";
  }

  private toValueConsideringRoundingError(value: number, exponent: number): number {
    const decimalPlace = Math.pow(10, exponent);
    return Math.round(value * decimalPlace) / decimalPlace;
  }

  private createBlockLineHeightCoefficientFromFontFamily(element: Element): number {
    const fontFamily = Enumerable.from(element.childNodes)
      .where(c => c.nodeType === 1)
      .select(c => (c as HTMLElement).style.fontFamily)
      .where(f => f !== "");
    return fontFamily.count() === 0
      ? this.createLineHeightCoefficientFromFontFamily(this.textStyle.fontFamily)
      : fontFamily.max(f => this.createLineHeightCoefficientFromFontFamily(f));
  }

  private createLineHeightCoefficientFromFontFamily(fontFamily: string): number {
    if (this.isLineSpacingNarrowForLogicalFont) { return 1; }

    // バックエンドのPDF生成ロジックを流用
    // 係数はバックエンドにも影響があるため修正時はお互いに合わせる(FontInformationConverter.cs参照)
    switch (fontFamily.replace(/"/g, "")) {
      case "monospace":
      case "Monospaced":
        return 1.365; // バックエンドの係数は1.31だが、合わないため調整
      case "SansSerif":
        return 1.26;
      case "Serif":
        return 1.26;
      case "Dialog":
        return 1.35;
      case "DialogInput":
        return 1.35;
      case "メイリオ":
        return 1.475; // バックエンドの係数は1.435だが、合わないため調整
      default:
        return 1;
    }
  }

  private createLineHeightCoefficientFromFontSize(fontSize: number): number {
    // TODO: リファクタ
    // 係数を算出で求めたいが14pxが特殊すぎて難しいと思われる
    // フォントや上付き等での係数未対応
    switch (fontSize) {
      case 8:
        return 0.6;
      case 10:
        return -0.1;
      case 12:
        return -0.8;
      case 14:
        return -2.5;
      case 16:
        return -2.2;
      case 18:
        return -2.9;
      case 20:
        return -3.6;
      case 22:
        return -4.3;
      case 24:
        return -5;
      case 26:
        return -5.7;
      case 28:
        return -6.4;
      case 36:
        return -9.3;
      case 48:
        return -13.4;
      case 72:
        return -21.9;
      case 108:
        return -34.5;
      case 156:
        return -51.6;
      case 216:
        return -72.6;
      default:
        return 0;
    }
  }

  private createLineHeightFromLineSpace(fontSize: number, lineSpace: number): number {
    // バックエンドのPDF生成ロジックを流用
    const value = this.toValueConsideringRoundingError(fontSize * lineSpace * 0.1, 1);
    const intValue = Math.floor(value);
    if (fontSize === 14 || lineSpace === 1) {
      // フォントサイズが14pxの場合 or 行間1の場合は切り捨て
      return intValue;
    } else if (lineSpace === 10) {
      // 行間10の場合+1
      return intValue + 1;
    } else if (value === intValue) {
      // 算出した値が整数ならそのまま
      return intValue;
    } else if (value % 4.8 === 0) {
      // 算出した値が4.8の倍数なら+1
      return intValue + 1;
    } else if (lineSpace > 5 && intValue % 2 === 0) {
      // 行間6以上で、算出した値の整数部分が偶数のとき+1
      return intValue + 1;
    } else {
      // 行間X-1の値を取得
      const lastValue = this.createLineHeightFromLineSpace(fontSize, lineSpace - 1);

      // 行間X-1の値と同じになる場合は+1する
      return lastValue === intValue
        ? intValue + 1
        : intValue;
    }
  }

  /** 外部テンプレート対応のI/Fで必要なので取り急ぎ定義しているが使っていない */
  setText(text: string): void {
    throw new Error("setText()は未実装です。");
  }
}
